import type {
  ProductOption,
  Variant,
} from '@bulknutrients/bulk-shared-lib/interfaces/Product';
import { ErrorAlert } from '@components/Alerts/ErrorAlert';
import { SuccessAlert } from '@components/Alerts/SuccessAlert';
import { Button, ButtonType } from '@components/Button';
import { Input } from '@components/FormComponents';
import { Paragraph } from '@components/Typography/Paragraph/Paragraph';
import { TextLink } from '@components/Typography/TextLink/TextLink';
import { useCustomer } from '@hooks/customer/useCustomer';
import { useKlaviyo } from '@hooks/useKlaviyo';
import { log } from '@lib/logs';
import dynamic from 'next/dynamic';
import { type ReactElement, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

const Modal = dynamic(() =>
  import('../Modal/Modal').then((mod) => mod.ModalNoPortal)
);

export function NotifyStockProduct({
  inStock,
  variantId,
  variants,
  name,
}: {
  inStock?: boolean;
  variantId?: number;
  variants: Variant[];
  name: string;
}): ReactElement {
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState(false);
  const [success, setSuccess] = useState(false);

  const { notifyStockSubscribe } = useKlaviyo();

  const rhfMethods = useForm<any>({
    mode: 'onTouched',
    defaultValues: {
      email: '',
    },
  });

  const handleClick = async (e) => {
    setShowForm(true);
  };

  const handleSubscribe = async (e: { email: string }) => {
    try {
      const variant = variantId || variants[0].variantId;

      const success = await notifyStockSubscribe({
        email: e.email,
        variantId: variant,
      });

      if (success) {
        setSuccess(true);
      } else {
        setError(true);
      }
      setShowForm(false);
    } catch (err) {
      log({ error: err, location: 'NotifyStock handleSubscribe' });
      setError(true);
    }
  };

  return !inStock &&
    inStock !== undefined &&
    (variants.length > 0 || variantId) ? (
    <div className="mx-0 my-5">
      <Button type={ButtonType.button} onClick={handleClick}>
        Notify me when in stock
      </Button>
      {showForm && (
        <Modal show={showForm} setShow={setShowForm}>
          <section>
            <h3>{name}</h3>
            <Paragraph>
              Register to receive a notification when this item comes back in
              stock.
            </Paragraph>
            <FormProvider {...rhfMethods}>
              <form
                className="flex flex-col gap-4"
                onSubmit={rhfMethods.handleSubmit(handleSubscribe)}
              >
                <Input
                  label="Email Address"
                  name="email"
                  type="email"
                  required
                  placeholder="Enter your email address"
                />
                <Button type={ButtonType.submit}>
                  Notify me when available
                </Button>
                <p className="text-sm text-grey-mid">
                  By submitting this form, you consent to receive marketing
                  materials (e.g. promos, cart reminders) from Bulk Nutrients at
                  the email address provided.{' '}
                  <TextLink href="/bulk-nutrients-privacy-policy">
                    Privacy Policy
                  </TextLink>{' '}
                  &{' '}
                  <TextLink href="/bulk-nutrients-terms-conditions">
                    Terms
                  </TextLink>
                  .
                </p>
              </form>
            </FormProvider>
          </section>
        </Modal>
      )}
      <SuccessAlert
        title=""
        message={`You are successfully subscribed and will receive an email when ${name} is back in stock`}
        show={success}
        setShow={setSuccess}
      />
      <ErrorAlert show={error} setShow={setError}>
        <Paragraph>Something went wrong trying to subscribe</Paragraph>
      </ErrorAlert>
    </div>
  ) : (
    <></>
  );
}

export function NotifyStockVariant({
  variants,
  stockLevels,
  options,
  availability,
}: {
  variants: Variant[];
  stockLevels: Record<number, boolean>;
  options: ProductOption[];
  availability?: string;
}): ReactElement {
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState(
    <Paragraph>Something went wrong trying to subscribe</Paragraph>
  );
  const [success, setSuccess] = useState(false);

  const { notifyStockSubscribe } = useKlaviyo();
  const { customer } = useCustomer();

  const rhfMethods = useForm<any>({
    mode: 'onTouched',
    defaultValues: {
      email: customer?.email || '',
    },
  });

  useEffect(() => {
    if (customer?.email) {
      rhfMethods.setValue('email', customer.email);
    }
  }, [customer?.email, rhfMethods]);

  const handleClick = async (e) => {
    setShowForm(true);
  };

  const handleSubscribe = async (e: {
    email: string;
    variantsToSubscribe: string | string[] | false;
  }) => {
    if (e.variantsToSubscribe === false) {
      setError(true);
      setErrorMessage(
        <Paragraph>
          You didn't select which option you want to subscribe to notifications
          for
        </Paragraph>
      );
      return;
    }
    try {
      const promises =
        typeof e.variantsToSubscribe === 'string'
          ? [
              notifyStockSubscribe({
                email: e.email,
                variantId: parseInt(e.variantsToSubscribe, 10),
              }),
            ]
          : e.variantsToSubscribe.map((variantId) =>
              notifyStockSubscribe({
                email: e.email,
                variantId: parseInt(variantId, 10),
              })
            );
      const success = await Promise.all(promises);

      if (success.every((s) => s)) {
        setSuccess(true);
        rhfMethods.reset();
      } else {
        setError(true);
        setErrorMessage(
          <Paragraph>
            Something went wrong adding you to that subscription list, please
            try again or{' '}
            <TextLink href="/contact-us">
              get in touch with customer service
            </TextLink>
          </Paragraph>
        );
      }
      setShowForm(false);
    } catch (err) {
      log({ error: err, location: 'NotifyStock handleSubscribe' });
      setError(true);
      setErrorMessage(
        <Paragraph>
          Something went wrong adding you to that subscription list, please try
          again or{' '}
          <TextLink href="/contact-us">
            get in touch with customer service
          </TextLink>
        </Paragraph>
      );
      setShowForm(false);
    }
  };

  return (
    <div className="mx-0 my-5">
      <Button
        type={ButtonType.button}
        buttonStyle="secondary"
        onClick={handleClick}
      >
        Subscribe for stock notifications
      </Button>
      {showForm && (
        <Modal show={showForm} setShow={setShowForm} className="max-w-[500px]">
          <section>
            <h3 className="mb-4">
              Get notified when your favourite option is back in stock!
            </h3>
            <FormProvider {...rhfMethods}>
              <form
                className="flex flex-col gap-4"
                onSubmit={rhfMethods.handleSubmit(handleSubscribe)}
              >
                <Input
                  label="Email Address"
                  name="email"
                  type="email"
                  required
                  placeholder="Enter your email address"
                />

                {availability && availability.length > 0 ? (
                  <p className="mb-4">{availability}</p>
                ) : null}

                <Input
                  label="Let me know when these options are back in stock"
                  name="variantsToSubscribe"
                  type="checkbox"
                  className="text-base p-0"
                  options={variants
                    .filter(
                      (variant) => stockLevels[variant.variantId] === false
                    )
                    .map((variant) => {
                      const label =
                        options.length > 1
                          ? variant.options
                              .reduce(
                                (acc, option) =>
                                  `${acc.length > 0 ? `${acc}, ` : ''}${option.displayName}: ${option.label}`,
                                ''
                              )
                              .trim()
                          : variant.options
                              .reduce(
                                (acc, option) =>
                                  `${acc.length > 0 ? `${acc}, ` : ''}${option.label}`,
                                ''
                              )
                              .trim();
                      return {
                        value: variant.variantId,
                        label: label,
                      };
                    })}
                />
                <Button type={ButtonType.submit} className="w-fit mx-auto">
                  Notify me
                </Button>
                <p className="text-sm dark:text-grey-mid">
                  By submitting this form, you consent to receive marketing
                  materials (e.g. promos, cart reminders) from Bulk Nutrients at
                  the email address provided.{' '}
                  <TextLink href="/bulk-nutrients-privacy-policy">
                    Privacy Policy
                  </TextLink>{' '}
                  &{' '}
                  <TextLink href="/bulk-nutrients-terms-conditions">
                    Terms
                  </TextLink>
                  .
                </p>
              </form>
            </FormProvider>
          </section>
        </Modal>
      )}
      <SuccessAlert
        title=""
        message={
          'You are successfully subscribed and will receive an email when your favourite option is back in stock'
        }
        show={success}
        setShow={setSuccess}
      />
      <ErrorAlert show={error} setShow={setError}>
        {errorMessage}
      </ErrorAlert>
    </div>
  );
}
